import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { useLoadScript } from '@react-google-maps/api';
import { theme } from './theme';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import HomePage from './pages/HomePage/HomePage';
import SignUp from './pages/Authentication/SignUp';
import LogIn from './pages/Authentication/LogIn';
import SettingsPage from './pages/Settings/SettingsPage';
import Facilities from './pages/Facilities/Facilities';
import FacilityDetail from './pages/Facilities/FacilityDetail';
import ResourceDetail from './pages/Facilities/ResourceDetail';
import EventDetail from './pages/Events/EventDetail';
import CreateFacilityResourceEvent from './pages/DataBuilderPage/CreateFacilityResourceEvent';
import ProtectedRoute from './components/ProtectedRoute';
import TopNavigation from './components/TopNavigation';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_JS_FRONTEND_KEY;
const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

// HomePage is accessible to all users (authenticated or not)

// Layout wrapper that includes navigation on all pages
const AppLayout = ({ children }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <TopNavigation />
      <Box
        component="main"
        sx={{
          flex: 1,
          overflow: 'auto',
          minWidth: 0, // Prevents flex item from overflowing
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

function AppRoutes({ isGoogleMapsLoaded, googleMapsLoadError }) {
  const { isAuthenticated, organization } = useAuth();

  return (
    <AppLayout>
      <Routes>
        {/* Public routes */}
        <Route 
          path="/" 
          element={
            <HomePage 
              isGoogleMapsLoaded={isGoogleMapsLoaded} 
              googleMapsLoadError={googleMapsLoadError} 
            />
          } 
        />
        <Route 
          path="/login" 
          element={
            isAuthenticated && organization ? (
              <Navigate to="/" replace />
            ) : (
              <LogIn />
            )
          } 
        />
        <Route 
          path="/signup" 
          element={
            isAuthenticated && organization ? (
              <Navigate to="/" replace />
            ) : (
              <SignUp />
            )
          } 
        />

        {/* Protected routes */}
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/facilities" element={<Facilities />} />
        <Route path="/facilities/:id" element={<FacilityDetail />} />
        <Route path="/resources/:id" element={<ResourceDetail />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/create-facility-resource-event" element={<CreateFacilityResourceEvent />} />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppLayout>
  );
}

function App() {
  // Load Google Maps script at app level with Places library
  // Use Places API key if available, otherwise fall back to Maps API key
  // NOTE: Environment variable must be named REACT_APP_GOOGLE_PLACES_API_KEY in .env file
  const apiKey = GOOGLE_PLACES_API_KEY || GOOGLE_MAPS_API_KEY || '';
  
  // Warn if Places API key is not set (only in development)
  if (process.env.NODE_ENV === 'development' && !GOOGLE_PLACES_API_KEY && GOOGLE_MAPS_API_KEY) {
    console.warn(
      '⚠️ GOOGLE_PLACES_API_KEY not found. Using GOOGLE_MAPS_API_KEY instead.\n' +
      'This may cause "ApiTargetBlockedMapError" if the Maps API key does not have Places API enabled.\n' +
      'To fix: Add REACT_APP_GOOGLE_PLACES_API_KEY=your_key to your .env file in the frontend directory and restart the dev server.'
    );
  }
  
  const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries: ['places'],
    version: 'weekly',
  });

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppRoutes 
            isGoogleMapsLoaded={isGoogleMapsLoaded} 
            googleMapsLoadError={googleMapsLoadError} 
          />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;

